import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/hero";
import { getMovieById, typeLabel } from "@/lib/movies";
import RatingBadge from "@/components/base/RatingBadge";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const total = heroSlides.length;

  const go = useCallback(
    (dir: number) => {
      setActive((prev) => (prev + dir + total) % total);
    },
    [total]
  );

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused, total]);

  const current = heroSlides[active];
  const movie = getMovieById(current.movieId);

  if (!movie) return null;

  return (
    <section
      className="w-full px-4 md:px-8 pt-6"
      aria-label="热门影视推荐"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="relative w-full h-[420px] md:h-[520px] rounded-lg overflow-hidden bg-primary-950">
          {heroSlides.map((slide, index) => (
            <img
              key={slide.movieId}
              src={slide.backdrop}
              alt={`${slide.tagline} 剧照`}
              title="天天影院热门影视"
              className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
                index === active ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}

          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-primary-950/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 via-primary-950/10 to-transparent" />

          <div className="absolute inset-x-0 bottom-0 p-5 md:p-8 lg:p-10">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="rounded-md bg-accent-500 text-background-50 text-xs px-2 py-0.5">
                {typeLabel(movie.type)}
              </span>
              {movie.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="rounded-md bg-background-50/15 text-background-100 text-xs px-2 py-0.5 backdrop-blur-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-3">
              {movie.title}
            </h2>
            <p className="text-background-200 text-sm md:text-base max-w-xl mb-4 line-clamp-2">
              {current.tagline}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <RatingBadge rating={movie.rating} />
              <span className="text-background-200 text-sm hidden sm:inline">
                {movie.genres.join(" · ")} · {movie.region} · {movie.year}
              </span>
              <Link
                to={`/detail/${movie.id}`}
                className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium px-4 py-2 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-fill" />
                查看详情
              </Link>
            </div>
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 left-3 md:left-5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(-1)}
              className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/80 hover:bg-background-50 text-primary-700 text-xl transition-colors cursor-pointer"
              aria-label="上一张"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
          </div>
          <div className="absolute top-1/2 -translate-y-1/2 right-3 md:right-5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(1)}
              className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/80 hover:bg-background-50 text-primary-700 text-xl transition-colors cursor-pointer"
              aria-label="下一张"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>

          <div className="absolute bottom-4 right-4 lg:hidden flex items-center gap-1.5">
            {heroSlides.map((slide, index) => (
              <button
                key={slide.movieId}
                type="button"
                onClick={() => setActive(index)}
                aria-label={`切换到第 ${index + 1} 张`}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  index === active ? "w-5 bg-accent-500" : "w-1.5 bg-background-50/60"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="hidden lg:flex flex-col gap-3">
          {heroSlides.map((slide, index) => {
            const item = getMovieById(slide.movieId);
            if (!item) return null;
            const isActive = index === active;
            return (
              <button
                key={slide.movieId}
                type="button"
                onClick={() => setActive(index)}
                className={`group flex flex-1 items-center gap-3 rounded-lg border p-2 text-left transition-all cursor-pointer ${
                  isActive
                    ? "border-accent-400 bg-background-50"
                    : "border-background-200 bg-background-50 hover:border-primary-300"
                }`}
              >
                <span className="w-14 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={item.cover}
                    alt={`${item.title} 海报`}
                    title={`${item.title} - 天天影院`}
                    className="w-full h-full object-cover object-top"
                  />
                </span>
                <span className="min-w-0">
                  <span
                    className={`block font-medium line-clamp-1 ${
                      isActive ? "text-primary-600" : "text-foreground-900"
                    }`}
                  >
                    {item.title}
                  </span>
                  <span className="mt-1 block text-xs text-foreground-500 line-clamp-2">
                    {slide.tagline}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}